<template>
    <im-input
        customContainerClass="number-input"
        v-bind="$attrs"
        :dataType="dataType"
        v-model:value="inputVal"
        @blur="onBlur"
        @focus="onFocus"
        :ref="onRef"
    >
        <template #prepend>
            <slot name="prepend">
                <span
                    v-if="dataType === 'money' && showMoney"
                    class="prepend money_prepend"
                >{{ currencySymbol }}</span>
            </slot>
        </template>
        <template #append>
            <slot name="append">
                <span v-if="dataType === 'days'" class="append days_append">{{
                    $t('md-search-bar.days')
                }}</span>
                <span v-if="dataType === 'sqft'" class="append sqft_append">{{
                    isSqft ? $t('md-search-bar.sqft') : $t('md-search-bar.acre')
                }}</span>
            </slot>
        </template>
    </im-input>
</template>

<script>
import util from '@/common/util';
import { mapState } from 'pinia';
import { usePageStore } from '@/store';
const SPLIT_REGEXP = /\B(?=(\d{3})+(?!\d))/g;
const FORMAT_STRATEGY = {
    // integer maxLength:9  split ,
    money(val) {
        val = val.replace(/\./g, '');
        val = val.substring(0, 9);
        val = val.replace(SPLIT_REGEXP, ',');
        return val;
    },
    // integer maxLength:4
    year(val) {
        val = val.replace(/\./g, '');
        val = val.substring(0, 4);
        return val;
    },
    // integer maxLength:9  split ,
    sqft(val, isSqft) {
        if (isSqft) {
            val = val.replace(/\./g, '');
            val = val.substring(0, 9);
            val = val.replace(SPLIT_REGEXP, ',');
        } else {
            val = FORMAT_STRATEGY.float(val);
        }
        return val;
    },
    // integer maxLength:6  split ,
    days(val) {
        val = val.replace(/\./g, '');
        val = val.substring(0, 6);
        val = val.replace(SPLIT_REGEXP, ',');
        return val;
    },
    float(val) {
        let [v1, v2] = val.split('.');
        if (v1) {
            v1 = v1.substring(0, 3);
        }
        if (v2) {
            v2 = v2.substring(0, 2);
        }
        return val.indexOf('.') > 0 ? `${v1}.${v2 || ''}` : `${v1}`;
    },
    none(val) {
        val = val.replace(/\./g, '');
        val = val.substring(0, 9);
        val = val.replace(SPLIT_REGEXP, ',');
        return val;
    }
};
export default {
    i18next: {
        ns: 'md-search-bar'
    },
    name: 'NumberInput',
    emits: ['numberBlur', 'numberFocus', 'update:value'],
    props: {
        value: {
            type: [String, Number],
            default: ''
        },
        dataType: {
            type: String,
            default: ''
        },
        isSqft: {
            type: Boolean,
            default: true
        },
        needCurrency: {
            type: Boolean,
            default: false
        }
    },
    inject: {
        moneyBase2showCache: {
            default: () => ({})
        }
    },
    computed: {
        ...mapState(usePageStore, {
            currencyRate: store => store.currencyRate,
            currencySymbol: store => store.currency.symbol,
            currencyAbbr: store => store.currency.abbr,
            baseCurrency: store => store.baseCurrency
        }),
        isCurCurrencyEqualBaseCurrency() {
            //  Determine whether the currently selected currency is equal to the base currency ；
            return this.currencyAbbr === this.baseCurrency;
        },
        inputVal: {
            get() {
                let value = this.value;
                if (
                    this.needCurrency &&
                    this.dataType === 'money' &&
                    !this.isCurCurrencyEqualBaseCurrency
                ) {
                    if (this.moneyBase2showCache && this.moneyBase2showCache[this.value]) {
                        return this.format(this.moneyBase2showCache[this.value])
                    }
                    if (
                        util.transferPriceToDollar({
                            price: this.tempVal,
                            isInt: true,
                            currencyRate: this.currencyRate
                        }) == value
                    ) {
                        value = this.tempVal;
                    } else {
                        value = util.transferDollarToCurrency({
                            price: value,
                            isInt: true,
                            currencyRate: this.currencyRate
                        });
                    }
                }
                return this.format(value);
            },
            set(val) {
                val = val.replaceAll(',', '');
                console.log(`set inputVal ${this.tempVal} => ${val}`);
                this.tempVal = val;
                if (
                    this.needCurrency &&
                    this.dataType === 'money' &&
                    !this.isCurCurrencyEqualBaseCurrency
                ) {
                    val = util.transferPriceToDollar({
                        price: val,
                        isInt: true,
                        currencyRate: this.currencyRate
                    });
                }
                val = this.format(val);
                this.$emit('update:value', val.replace(/[^\d.]/g, ''));
            }
        },
        showMoney() {
            if (this.value || this.inputState === 'focus') {
                return true;
            } else {
                return false;
            }
        }
    },
    data: function() {
        return {
            inputState: 'blur',
            tempVal: this.value,
            inputRef: null
        };
    },
    methods: {
        onRef(e) {
            this.inputRef = e;
        },
        inputFocus() {
            this.inputRef.focus();
            this.onFocus();
        },
        onBlur() {
            this.inputState = 'blur';
            this.$emit('numberBlur');
        },
        onFocus() {
            this.inputState = 'focus';
            this.$emit('numberFocus');
        },
        format(val) {
            if (val === '') {
                return val; 
            }
            //  There can only be one at the beginning  0
            val = String(val);
            if (val.indexOf('00') === 0) {
                return '0'; 
            }
            //  remove illegal numeric characters
            val = val
                .replace(/[^\d.]/g, '')
                .replace(/^(\d+\.\d*)(\.+\d*)+/, '$1');
            return (
                (FORMAT_STRATEGY[this.dataType] &&
                    FORMAT_STRATEGY[this.dataType](val, this.isSqft)) ||
                val
            );
        }
    }
};
</script>

<style lang="scss">
.number-input{
    .prepend {
        padding-left: 16px;
        color: #c6c8d1;
    }
    .append {
        padding-right: 16px;
        color: #c6c8d1;
    }
    .money_prepend {
        color: #000;
        /* font-weight: bold; */
    }
}
</style>
